import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, Subject, takeUntil } from 'rxjs';
import { isAppInMaintenance } from 'app/shared/maintenance/app-maintenance.util';
import { FuseConfigService } from '@fuse/services/config';
import { ToasterMessage } from './app.types';
import { SharedService } from './shared/shared.service';
import { HtmlSnackbarComponent } from './shared/html-snackbar/html-snackbar.component';
import { Profile } from './modules/profile/profile.model';
import { ProfileService } from './modules/profile/profile.service';
import { AuthUtils } from 'app/authentication/auth/auth.utils';
import { AuthService } from 'app/authentication/auth/auth.service';
import { environment } from 'environments/environment';
import { selectableOrganizations } from 'app/shared/entitlement/domain-organization.util';
import { resolveWorkspaceLifecycle } from 'app/shared/lifecycle/lifecycle.util';
import { WorkspaceLifecycleService } from 'app/shared/lifecycle/workspace-lifecycle.service';
import { WorkspaceLifecycle } from 'app/app.types';
import { isExternalStoreHost, isServicesBoHost } from 'app/modules/services-store/services-host.util';
import { applyLayoutBrandColors } from 'app/shared/branding/tenant-brand-colors.util';

type SessionSelectionStep = 'organization' | 'branch' | 'no-organization' | 'no-branch';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    showSelectionOverlay = false;
    selectionStep: SessionSelectionStep = 'organization';
    user: Profile | null = null;
    organizations: any[] = [];
    selectedOrganization: any = null;
    selectedBranch: any = null;
    activeOrganization: any = null;
    currentUrl = '';
    impersonating = false;
    workspaceLifecycle: WorkspaceLifecycle | null = null;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _snackBar: MatSnackBar,
        private _sharedService: SharedService,
        private _router: Router,
        private _profileService: ProfileService,
        private _fuseConfigService: FuseConfigService,
        private _authService: AuthService,
        private _lifecycleService: WorkspaceLifecycleService,
    ) {
    }

    ngOnInit(): void {
        applyLayoutBrandColors(environment.layout);
        this._hydrateSessionFromStorage();
        this.currentUrl = this._router.url || '';
        this.impersonating = environment.user.impersonator != undefined;

        this._refreshWorkspaceLifecycle();

        this._router.events
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((event) => {
                if (event instanceof NavigationStart && this._shouldShowPageLoader(event.url)) {
                    this._sharedService.show_loader();
                }

                if (event instanceof NavigationCancel || event instanceof NavigationError) {
                    this._sharedService.hide_loader();
                }

                if (event instanceof NavigationEnd) {
                    this.currentUrl = event.urlAfterRedirects;
                    this._sharedService.hide_loader();
                    this._evaluateSelectionOverlay();
                }
            });

        this._profileService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: Profile) => {
                this.user = user;
                this.impersonating = environment.user.impersonator != undefined;
                this._evaluateSelectionOverlay();
            });

        this._profileService.organizations$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => this._refreshWorkspaceLifecycle());

        this._profileService.organizations$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((organizations: any[]) => {
                this.organizations = Array.isArray(organizations) ? organizations : [];
                this._evaluateSelectionOverlay();
            });

        this._profileService.selectedOrganization$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((organization: any) => {
                this.selectedOrganization = organization;
                this._refreshWorkspaceLifecycle();
                if (!organization) {
                    this.activeOrganization = null;
                }
                this._evaluateSelectionOverlay();
            });

        this._profileService.selectedBranch$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((branch: any) => {
                this.selectedBranch = branch;
                this._refreshWorkspaceLifecycle();
                this._evaluateSelectionOverlay();
            });
    }

    ngAfterViewInit(): void {
        this._sharedService.message$
            .pipe(
                filter((message): message is ToasterMessage => !!message && !!message.message && message.message.trim().length > 0),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((message) => {
                this._snackBar.openFromComponent(HtmlSnackbarComponent, {
                    data: { message: message.message.trim() },
                    duration: 5000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['app-toast', `app-toast-${message.type}`]
                });
            });
    }

    chooseOrganization(organization: any): void {
        if (!organization) {
            return;
        }

        this._setWorkspaceSelection(organization, null);
        this.activeOrganization = organization;

        const branches = organization?.branches || [];
        if (branches.length === 1) {
            this._applySelection(branches[0], organization);
            return;
        }

        this.selectionStep = branches.length === 0 ? 'no-branch' : 'branch';
        this.showSelectionOverlay = true;
    }

    selectBranch(branch: any): void {
        if (!this.activeOrganization || !branch) {
            return;
        }

        this._applySelection(branch, this.activeOrganization);
    }

    backToOrganizations(): void {
        this.activeOrganization = null;
        this._setWorkspaceSelection(null, null);
        this.selectionStep = 'organization';
        this.showSelectionOverlay = true;
    }

    signOut(): void {
        window.location.assign('/auth/sign-out');
    }

    unimpersonate(): void {
        this._authService
            .unimpersonate()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((ok) => {
                if (!ok) {
                    return;
                }

                this.impersonating = false;
                window.location.assign('/dashboard');
            });
    }

    trackByCode(index: number, item: any): string {
        return item?.code || `${index}`;
    }

    get activeBranches(): any[] {
        return this.activeOrganization?.branches || [];
    }

    get isWorkspaceBlocked(): boolean {
        return this._lifecycleService.isBlocked || !!this.workspaceLifecycle?.blocked;
    }

    get workspaceBlockedMessage(): string {
        return this._lifecycleService.blockedMessage
            || this.workspaceLifecycle?.banner?.message
            || 'This workspace is currently blocked.';
    }

    get isAppInMaintenance(): boolean {
        return isAppInMaintenance();
    }

    get shouldShowPlatformMessage(): boolean {
        if (isServicesBoHost() || isExternalStoreHost()) {
            return false;
        }
        return !!this._authService.accessToken && !this._isAuthRoute(this.currentUrl);
    }

    get shouldRenderWorkspaceContent(): boolean {
        const isAuthRoute = this._isAuthRoute(this.currentUrl);

        if (isAuthRoute) {
            return true;
        }

        const hasAccessToken = !!this._authService.accessToken;
        if (!hasAccessToken) {
            return true;
        }

        if (this._isSuperAdminUser()) {
            return true;
        }

        if (this.isWorkspaceBlocked) {
            return false;
        }

        return !!(this.selectedOrganization && this.selectedBranch);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    private _shouldShowPageLoader(url: string): boolean {
        const cleanUrl = `${url || ''}`.split('?')[0];

        return cleanUrl === '/profile'
            || cleanUrl.startsWith('/profile/')
            || cleanUrl === '/dashboard'
            || cleanUrl.startsWith('/dashboard/');
    }

    private _evaluateSelectionOverlay(): void {
        if (this._isAuthRoute(this.currentUrl)) {
            this.showSelectionOverlay = false;
            this.activeOrganization = null;
            return;
        }

        const hasAccessToken = !!this._authService.accessToken;
        if (!hasAccessToken) {
            this.showSelectionOverlay = false;
            this.activeOrganization = null;
            return;
        }

        if (this._isSuperAdminUser()) {
            this.showSelectionOverlay = false;
            this.activeOrganization = null;
            return;
        }

        const organizations = Array.isArray(this.organizations) ? this.organizations : [];

        if (!organizations.length) {
            this._setWorkspaceSelection(null, null);
            this.selectionStep = 'no-organization';
            this.showSelectionOverlay = true;
            this.activeOrganization = null;
            return;
        }

        if (organizations.length === 1) {
            this._handleSingleOrganizationFlow(organizations[0]);
            return;
        }

        if (!this.selectedOrganization) {
            this.selectionStep = 'organization';
            this.showSelectionOverlay = true;
            this.activeOrganization = null;
            return;
        }

        const activeOrganization = organizations.find((item: any) => item?.code === this.selectedOrganization?.code) || null;

        if (!activeOrganization) {
            this._setWorkspaceSelection(null, null);
            this.selectionStep = 'organization';
            this.showSelectionOverlay = true;
            this.activeOrganization = null;
            return;
        }

        const branches = Array.isArray(activeOrganization?.branches) ? activeOrganization.branches : [];

        this.activeOrganization = activeOrganization;

        if (!branches.length) {
            this._setWorkspaceSelection(activeOrganization, null);
            this.selectionStep = 'no-branch';
            this.showSelectionOverlay = true;
            return;
        }

        if (branches.length === 1) {
            const singleBranch = branches[0];
            const shouldApply = this.selectedBranch?.code !== singleBranch?.code
                || this.selectedOrganization?.code !== activeOrganization?.code;

            if (shouldApply) {
                this._applySelection(singleBranch, activeOrganization);
                return;
            }

            this.showSelectionOverlay = false;
            return;
        }

        if (!this.selectedBranch) {
            this._setWorkspaceSelection(activeOrganization, null);
            this.selectionStep = 'branch';
            this.showSelectionOverlay = true;
            return;
        }

        const matchedBranch = branches.find((item: any) => item?.code === this.selectedBranch?.code) || null;
        if (!matchedBranch) {
            this.selectionStep = 'branch';
            this.showSelectionOverlay = true;
            this._setWorkspaceSelection(activeOrganization, null);
            return;
        }

        this.showSelectionOverlay = false;
    }

    private _applySelection(branch: any, organization: any): void {
        if (!organization) {
            return;
        }

        this.activeOrganization = organization;
        this._setWorkspaceSelection(organization, branch ?? null);
        this.showSelectionOverlay = false;
    }

    private _handleSingleOrganizationFlow(organization: any): void {
        const activeOrganization = organization || null;
        this.activeOrganization = activeOrganization;

        const branches = Array.isArray(activeOrganization?.branches) ? activeOrganization.branches : [];

        if (!activeOrganization) {
            this._setWorkspaceSelection(null, null);
            this.selectionStep = 'no-organization';
            this.showSelectionOverlay = true;
            return;
        }

        if (!branches.length) {
            this._setWorkspaceSelection(activeOrganization, null);
            this.selectionStep = 'no-branch';
            this.showSelectionOverlay = true;
            return;
        }

        if (branches.length === 1) {
            const singleBranch = branches[0];
            const shouldApply = this.selectedOrganization?.code !== activeOrganization?.code
                || this.selectedBranch?.code !== singleBranch?.code;

            if (shouldApply) {
                this._applySelection(singleBranch, activeOrganization);
                return;
            }

            this.showSelectionOverlay = false;
            return;
        }

        const matchedBranch = branches.find((item: any) => item?.code === this.selectedBranch?.code) || null;
        this._setWorkspaceSelection(activeOrganization, matchedBranch);

        if (matchedBranch) {
            this.showSelectionOverlay = false;
            return;
        }

        this.selectionStep = 'branch';
        this.showSelectionOverlay = true;
    }

    private _setWorkspaceSelection(organization: any, branch: any): void {
        const normalizedOrganization = organization ?? null;
        const normalizedBranch = branch ?? null;

        const organizationChanged = !this._isSameSelectionEntity(this.selectedOrganization, normalizedOrganization);
        const branchChanged = !this._isSameSelectionEntity(this.selectedBranch, normalizedBranch);

        // Prevent recursive emissions when selection did not effectively change.
        if (organizationChanged) {
            this._profileService.selectedOrganization = normalizedOrganization;
        }

        if (branchChanged) {
            this._profileService.selectedBranch = normalizedBranch;
        }

        // this._syncUserRoleForSelection(organization ?? null, branch ?? null);
        this._applyWorkspaceConfig(normalizedOrganization, normalizedBranch);
    }

    private _isSameSelectionEntity(current: any, next: any): boolean {
        if (!current && !next) {
            return true;
        }

        if (!current || !next) {
            return false;
        }

        const currentCode = current?.code ?? null;
        const nextCode = next?.code ?? null;

        if (currentCode !== null || nextCode !== null) {
            return currentCode === nextCode;
        }

        const currentId = current?.id ?? null;
        const nextId = next?.id ?? null;

        if (currentId !== null || nextId !== null) {
            return currentId === nextId;
        }

        return current === next;
    }

    private _syncSelectionIndexesFromAccess(organization: any, branch: any): void {
        // No-op: selection is stored as objects in environment.selection.organization/branch
        // via the ProfileService setters. No index tracking needed.
    }

    // private _syncUserRoleForSelection(organization: any, branch: any): void {
    //     const roleMap = this.user?.extra?.role_map
    //         || (environment.user?.access && Object.keys(environment.user.access).length > 0 ? environment.user.access : null)
    //         || this.user?.role;

    //     if (!this.user || !roleMap) {
    //         return;
    //     }

    //     const resolvedRoles = AuthUtils.resolveEffectiveRoles(roleMap, {
    //         selectedOrganizationCode: organization?.code || null,
    //         selectedBranchCode: branch?.code || null,
    //         fallbackOrganizationCode: organization?.code || this.organizations?.[0]?.code || null,
    //         user: this.user
    //     });

    //     const currentRoles = Array.isArray(this.user?.role)
    //         ? this.user.role
    //         : AuthUtils.normalizeRoles(this.user?.role);

    //     const effectiveRoles = resolvedRoles.length > 0 ? resolvedRoles : currentRoles;

    //     if (environment.user) {
    //         environment.user = {
    //             ...environment.user,
    //             roles: {
    //                 super: AuthUtils.hasRole(effectiveRoles, ['@SUPERADMIN'], this.user),
    //                 admin: AuthUtils.hasRole(effectiveRoles, ['@ADMIN'], this.user),
    //                 manager: AuthUtils.hasRole(effectiveRoles, ['@MANAGER'], this.user),
    //                 user: AuthUtils.hasRole(effectiveRoles, ['@USER'], this.user)
    //             }
    //         };
    //     }

    //     if (JSON.stringify(currentRoles) === JSON.stringify(effectiveRoles)) {
    //         return;
    //     }

    //     const updatedUser: Profile = { ...this.user };
    //     this.user = updatedUser;
    //     this._profileService.user = updatedUser;
    // }

    private _hydrateSessionFromStorage(): void {
        this.user = (environment.user?.profile ?? null) as any;
        this.organizations = selectableOrganizations(environment.user?.organizations);
        this.selectedOrganization = environment.selection.organization || null;
        this.selectedBranch = environment.selection.branch || null;

        if (this.user) {
            this._profileService.user = this.user;
        }

        this._profileService.organizations = this.organizations;
        this._profileService.selectedOrganization = this.selectedOrganization;
        this._profileService.selectedBranch = this.selectedBranch;
    }

    private _isAuthRoute(url: string): boolean {
        // Auth UI is served by the API at /auth/* (outside this SPA).
        return `${url || ''}`.startsWith('/auth');
    }

    private _isSuperAdminUser(): boolean {
        return environment.user?.superadmin === true || environment.user?.roles?.super === true;
    }

    private _applyWorkspaceConfig(organization: any, branch: any): void {
        const config = branch?.config || organization?.config;

        if (config) {
            this._fuseConfigService.applyPermanentLayoutChanges(config);
            return;
        }
    }

    private _refreshWorkspaceLifecycle(): void {
        const resolved = resolveWorkspaceLifecycle(
            this.selectedOrganization,
            this.selectedBranch
        );
        this.workspaceLifecycle = resolved;
        this._lifecycleService.clearForcedFlags();
        this._lifecycleService.applyLifecycle(resolved);
    }

}
